import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { SettingsTabs } from "@/components/settings/settings-tabs";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { Form } from "@/components/form/form";
import { FormTextField } from "@/components/form/fields";
import {
  ipAllowlistApi,
  useSessions,
  useSessionMutations,
  type ipEntrySchema,
  type SessionEntry,
} from "@/api/settings";

export const Route = createFileRoute("/_app/settings/security")({
  component: SecuritySettings,
});

type IpEntry = z.infer<typeof ipEntrySchema>;

function SecuritySettings() {
  return (
    <PageContainer>
      <PageHeader
        title="Security"
        description="Network allowlisting, active sessions and multi-factor authentication."
      />
      <SettingsTabs
        tabs={[
          { value: "ip", label: "IP allowlist", render: () => <IpTab /> },
          { value: "sessions", label: "Sessions", render: () => <SessionsTab /> },
          { value: "mfa", label: "MFA", render: () => <MfaTab /> },
        ]}
      />
    </PageContainer>
  );
}

const ipSchema = z.object({
  cidr: z.string().regex(/^\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?$/, "e.g. 203.0.113.0/24"),
  description: z.string().optional(),
});

function IpTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = ipAllowlistApi.useList();
  const m = ipAllowlistApi.useMutations();
  const [adding, setAdding] = useState(false);
  const form = useForm<z.infer<typeof ipSchema>>({
    resolver: zodResolver(ipSchema),
    defaultValues: { cidr: "", description: "" },
  });

  const columns: Column<IpEntry>[] = [
    { id: "cidr", header: "CIDR", cell: (r) => <MonoText>{r.cidr}</MonoText> },
    { id: "desc", header: "Description", cell: (r) => r.description ?? "—" },
    {
      id: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled === false ? (
          <Chip size="small" label="Disabled" />
        ) : (
          <Chip size="small" color="success" label="Active" />
        ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <IconButton
          size="small"
          color="error"
          aria-label="Delete entry"
          onClick={async (e) => {
            e.stopPropagation();
            if (await confirm({ title: `Remove ${r.cidr} from allowlist?`, destructive: true })) {
              await m.remove.mutateAsync(r.id);
              toast.success("Removed");
            }
          }}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      ),
    },
  ];

  async function onAdd(values: z.infer<typeof ipSchema>) {
    await m.create.mutateAsync({ ...values, enabled: true });
    toast.success("Range added");
    setAdding(false);
    form.reset();
  }

  return (
    <>
      <Card>
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          toolbar={
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add range
            </Button>
          }
          empty={
            <EmptyState
              title="Allowlist is empty"
              description="With no ranges, sign-in is allowed from any address. Add a range to restrict access."
            />
          }
        />
      </Card>
      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add IP range</DialogTitle>
        <Form form={form} onSubmit={onAdd}>
          <DialogContent>
            <Stack spacing={2}>
              <FormTextField
                control={form.control}
                name="cidr"
                label="CIDR"
                required
                placeholder="203.0.113.0/24"
              />
              <FormTextField control={form.control} name="description" label="Description" />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
              Add
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
}

function SessionsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = useSessions();
  const m = useSessionMutations();

  const columns: Column<SessionEntry>[] = [
    { id: "device", header: "Device", cell: (r) => r.deviceName ?? r.userAgent ?? "Unknown" },
    { id: "ip", header: "IP", cell: (r) => <MonoText>{r.ipAddress ?? "—"}</MonoText> },
    { id: "active", header: "Last active", cell: (r) => <RelativeTime value={r.lastActiveAt} /> },
    { id: "created", header: "Started", cell: (r) => <RelativeTime value={r.createdAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <Button
          size="small"
          color="error"
          onClick={async (e) => {
            e.stopPropagation();
            if (await confirm({ title: "Revoke this session?", destructive: true })) {
              await m.revoke.mutateAsync(r.id);
              toast.success("Session revoked");
            }
          }}
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        toolbar={
          <Button
            size="small"
            color="error"
            onClick={async () => {
              if (await confirm({ title: "Revoke all other sessions?", destructive: true })) {
                await m.revokeAll.mutateAsync();
                toast.success("Other sessions revoked");
              }
            }}
          >
            Revoke all others
          </Button>
        }
        empty={<EmptyState title="No active sessions" />}
      />
    </Card>
  );
}

function MfaTab() {
  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
          <Typography variant="subtitle1">Multi-factor authentication</Typography>
          <Typography variant="body2" color="text.secondary">
            Add a TOTP authenticator to your account from the profile menu to require a second
            factor at sign-in. Per-user MFA status is shown on the Users tab.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tenant-wide MFA enforcement is configured by a system administrator.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
